import { IconButton, mergeStyleSets, Text, TextField } from "@fluentui/react";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useMask from "react-mask-hook";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { FieldContainer } from "./FieldContainer";
import { TextFieldOptionsButton } from "./TextFieldOptionsButton";

const getClasses = (theme, isDarkMode, disabled) =>
  mergeStyleSets({
    label: {
      fontSize: "14px",
      fontWeight: "600",
      padding: "5px",
      display: "block",
      color: (function () {
        if (disabled) {
          return theme.palette.neutralSecondaryAlt;
        }
        if (isDarkMode) {
          return "white";
        }
        return theme.palette.black;
      })(),
    },
    labelAsterix: {
      color: theme.palette.redDark,
    },
  });

const getTextFieldStyle = (transparent, theme, isMarked, confidentiality) => ({
  root: {
    ".ms-TextField-fieldGroup": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
      background: transparent ? "transparent" : "inherit",
      ...(isMarked && { background: theme.palette.yellowLight }),
      ...(confidentiality && {
        background: `rgb(${confidentiality < 0.9 ? 255 : 200},${
          confidentiality >= 0.8 ? 255 : 200
        },200)`,
      }),
    },
    ".ms-TextField-fieldGroup::after": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
    },
    ".ms-TextField-fieldGroup input": {
      color: isMarked
        ? "black"
        : theme.isDark
          ? confidentiality
            ? "black"
            : "white"
          : "black",
    },

    ".ms-TextField-fieldGroup input:disabled": {
      background: transparent ? "transparent" : "inherit",
      color: theme.palette.neutralSecondaryAlt,
    },
    ".ms-TextField-fieldGroup textarea:disabled": {
      background: theme.palette.white,
    },
  },
  suffix: {
    background: "transparent",
    padding: 0,
  },
});

type Props = {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name: string;
  disabled?: boolean;
  label: string;
  errors?: any;
  transparent?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  isMarked?: boolean;
  confidentiality?: number;
  // optional icon button rendered next to the field (independent of the field's disabled state)
  actionButton?: {
    iconName: string;
    title: string;
    onClick: () => void;
    disabled?: boolean;
  };
  // values offered in a drop-down next to the input (the field stays editable)
  options?: string[];
  [x: string]: any; //rest props
};

const TextInputField = ({
  disabled,
  label,
  name,
  onChange,
  error = "",
  value,
  errors,
  transparent,
  required,
  isMarked,
  confidentiality,
  isDayMonthField,
  actionButton,
  options,
  ...props
}: Props) => {
  const [textValue, setTextValue] = useState("");
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const isDarkMode = theme.isDark;

  const classes = getClasses(theme, isDarkMode, disabled);

  const styles = getTextFieldStyle(
    transparent,
    theme,
    isMarked,
    confidentiality,
  );

  function handleMaskChange(value) {
    setTextValue(value);
  }

  function handleChange(event) {
    setTextValue(event.target.value);
  }

  const maskProps = useMask({
    value: textValue ? textValue?.replace("/", "") : "",
    onChange: handleMaskChange,
    // array of regexp and formatting characters
    mask: "##/##",
    placeholder: "DD/MM",
  });

  useEffect(() => {
    setTextValue(value);
  }, [value]);

  const textField = (
    <TextField
      key={value}
      id={name + "-input"}
      name={name}
      onBlur={(ev) => {
        onChange(ev);
      }}
      onRenderLabel={() => (
        <Text className={classes.label}>
          {t(label)}{" "}
          <Text className={classes.labelAsterix}>{required && "*"}</Text>
        </Text>
      )}
      styles={styles}
      disabled={disabled}
      label={t(label)}
      onChange={handleChange}
      {...(!isDayMonthField && {
        defaultValue: value ?? "",
      })}
      errorMessage={name && errors[name]}
      autoComplete="off"
      {...(isDayMonthField && (maskProps as any))}
      {...(!!options?.length && {
        onRenderSuffix: () => (
          <TextFieldOptionsButton
            options={options}
            target={`#${name}-input`}
            disabled={disabled}
            ariaLabel={t(label)}
            onSelect={(option) =>
              onChange({
                target: { name, value: option },
              } as React.ChangeEvent<any>)
            }
          />
        ),
      })}
      {...props}
    />
  );

  return (
    <FieldContainer isTooltipHidden={disabled} tooltipText={label}>
      {actionButton ? (
        <div style={{ display: "flex", alignItems: "end", width: "100%" }}>
          <div style={{ flexGrow: 1 }}>{textField}</div>
          <IconButton
            iconProps={{ iconName: actionButton.iconName }}
            title={actionButton.title}
            ariaLabel={actionButton.title}
            onClick={actionButton.onClick}
            disabled={actionButton.disabled}
            style={{ flexShrink: 0 }}
          />
        </div>
      ) : (
        textField
      )}
    </FieldContainer>
  );
};

export default TextInputField;
